import { joinCommasAnd } from "@outschool/text";
import {
  ApolloError,
  gql,
  useMutation,
  useQueryWithPreviousData
} from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { useCallback } from "react";

import {
  BulkPricingBreakdownQuery as BulkPricingBreakdownQueryType,
  BulkPricingBreakdownQueryVariables,
  CreateOrderMutationMutation,
  CreateOrderMutationMutationVariables,
  PricingBreakdownQueryQuery,
  PricingBreakdownQueryQueryVariables
} from "../../shared/generated/graphql";
import { usePaymentMethods } from "./CurrentUser";

export const createOrderMutation = gql`
  mutation CreateOrderMutation {
    createOrder {
      orderSlugId
    }
  }
`;

export function useCreateOrder() {
  const [createOrder] = useMutation<
    CreateOrderMutationMutation,
    CreateOrderMutationMutationVariables
  >(createOrderMutation);
  return createOrder;
}

export const PricingBreakdownQuery = gql`
  query PricingBreakdownQuery($pricingBreakdownInput: PricingBreakdownInput!) {
    pricingBreakdown(pricingBreakdownInput: $pricingBreakdownInput) {
      originalTotalCents
      totalCents
      pricingOfferCents
      pricingOfferState
      storedCreditCents
      creditAccountState
      stripeCents
      couponCents
      couponCredits
      couponState
      enrollmentMeetingTotalCents
      perEnrollmentCurrentWeekCents
      perEnrollmentUpfrontCents
      perEnrollmentWeeklyCents
      perEnrollmentTotalWeeklyCents
      hasProratedEnrollments
      perEnrollmentUpfrontCredits
      perEnrollmentWeeklyCredits
      perEnrollmentTotalWeeklyCredits
      perEnrollmentCurrentWeekCredits
      totalCredits
      serviceFeePercentage
      esaMembershipCredits
      esaMembershipAccountState
      esaMembershipFundingProgram
      subscriptionCredits
      subscriptionAccountState
      subscriptionOutstandingBalanceCents
      subscriptionOutstandingBalanceCredits
      pricingOffer {
        uid
        name
        userOfferEmail
        userLimitCents
        requirePaymentMethod
        buyerOrgName
        remainingCapCents
        organization {
          name
        }
      }
    }
  }
`;

export function usePricingBreakdown(
  pricingBreakdownInput: PricingBreakdownQueryQueryVariables["pricingBreakdownInput"]
): {
  error: ApolloError | undefined;
  pricingBreakdown: PricingBreakdownQueryQuery["pricingBreakdown"];
  loading: boolean;
  refetch: () => Promise<void>;
} {
  const {
    error,
    loading,
    data,
    refetch: refetchRaw
  } = useQueryWithPreviousData<
    PricingBreakdownQueryQuery,
    PricingBreakdownQueryQueryVariables
  >(PricingBreakdownQuery, {
    fetchPolicy: "cache-and-network",
    variables: { pricingBreakdownInput },
    skip:
      !pricingBreakdownInput.enrollmentCount &&
      !pricingBreakdownInput.numCredits
  });
  const pricingBreakdown = data?.pricingBreakdown!;
  const refetch = useCallback(async () => {
    if (
      !pricingBreakdownInput.enrollmentCount &&
      !pricingBreakdownInput.numCredits
    ) {
      return;
    }
    refetchRaw({ pricingBreakdownInput });
  }, [pricingBreakdownInput, refetchRaw]);
  return {
    error,
    pricingBreakdown,
    loading,
    refetch
  };
}

const BulkPricingBreakdownQuery = gql`
  query BulkPricingBreakdown(
    $pricingBreakdownInputs: [PricingBreakdownInput!]!
    $outschoolCouponCode: String
  ) {
    bulkPricingBreakdown(
      pricingBreakdownInputs: $pricingBreakdownInputs
      outschoolCouponCode: $outschoolCouponCode
    ) {
      itemizedPricingBreakdown {
        uid
        totalCents
        pricingOfferState
        perEnrollmentUpfrontCents
        perEnrollmentWeeklyCents
        perEnrollmentCurrentWeekCents
        perEnrollmentTotalWeeklyCents
        hasProratedEnrollments
        couponCents
        couponState
      }
      originalTotalCents
      totalCents
      pricingOfferCents
      totalPayUpfrontCents
      totalWeeklyFirstPaymentCents
      pricingOffer {
        uid
        name
        userOfferEmail
        userLimitCents
        requirePaymentMethod
        buyerOrgName
        remainingCapCents
        organization {
          name
        }
      }
      storedCreditCents
      stripeCents
      esaMembershipCredits
      subscriptionCredits
      subscriptionOutstandingBalanceCents
      subscriptionOutstandingBalanceCredits
      couponCents
      couponState
      weeklyPaymentsCharges {
        priceCents
        startOfWeek
      }
    }
  }
`;

export function useBulkPricingBreakdown(
  pricingBreakdownInputs: PricingBreakdownQueryQueryVariables["pricingBreakdownInput"][],
  outschoolCouponCode?: string
): {
  error: ApolloError | undefined;
  pricingBreakdown:
    | BulkPricingBreakdownQueryType["bulkPricingBreakdown"]
    | undefined;
  loading: boolean;
  refetch: () => Promise<void>;
} {
  const {
    error,
    loading,
    data,
    refetch: refetchRaw
  } = useQueryWithPreviousData<
    BulkPricingBreakdownQueryType,
    BulkPricingBreakdownQueryVariables
  >(BulkPricingBreakdownQuery, {
    fetchPolicy: "cache-and-network",
    variables: { pricingBreakdownInputs, outschoolCouponCode },
    skip:
      !pricingBreakdownInputs.length ||
      pricingBreakdownInputs.every(input => !input?.enrollmentCount)
  });
  const pricingBreakdown = data?.bulkPricingBreakdown;
  const refetch = useCallback(async () => {
    if (
      pricingBreakdownInputs.length ||
      pricingBreakdownInputs.every(input => !input?.enrollmentCount)
    ) {
      return;
    }
    refetchRaw({ pricingBreakdownInputs, outschoolCouponCode });
  }, [pricingBreakdownInputs, outschoolCouponCode, refetchRaw]);
  return {
    error,
    pricingBreakdown,
    loading,
    refetch
  };
}

export function usePaymentText(sectionUid: string): {
  paymentText: string | null;
  loading: boolean;
} {
  const { currentUser, currentUserHasLoaded } = useSession();
  const { paymentMethods, loading: isPaymentMethodsLoading } =
    usePaymentMethods();
  const last4 = paymentMethods?.[0]?.last4;
  const { pricingBreakdown, loading: isPricingBreakdownLoading } =
    usePricingBreakdown({
      sectionUid,
      buyingUserUid: currentUser?.uid,
      enrollmentCount: 1
    });
  if (
    !currentUserHasLoaded ||
    isPaymentMethodsLoading ||
    isPricingBreakdownLoading
  ) {
    return {
      loading: true,
      paymentText: null
    };
  }
  if (!pricingBreakdown) {
    return { paymentText: null, loading: false };
  }
  const paymentTexts = [];
  if (pricingBreakdown.pricingOfferCents > 0) {
    // @ts-ignore TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
    paymentTexts.push(`${pricingBreakdown.pricingOffer?.buyerOrgName} funds`);
  }
  if (pricingBreakdown.storedCreditCents > 0) {
    // @ts-ignore TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
    paymentTexts.push("Outschool Credits");
  }
  if (pricingBreakdown.stripeCents > 0) {
    if (!last4) {
      return { paymentText: "your card", loading: false };
    }
    // @ts-ignore TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
    paymentTexts.push(`your card ending in ${last4}`);
  }
  return { paymentText: joinCommasAnd(paymentTexts), loading: false };
}
